import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { PromptService } from '../../components/prompt/prompt.service';
import { TranslationPipe } from '../../features/translate/translation.pipe';
import { logErrorInSentry } from '../../modules/errors/response-error';
import { TPointSearchResponse } from '../../modules/points/point-search/point-search.consts';
import {
  TInfiniteScrollData,
  TInfiniteScrollingRequestData,
  TInfiniteScrollQuery,
  TInfiniteScrollRequest,
} from './infinite-scrolling.consts';

@Injectable({
  providedIn: 'root',
})
export class InfiniteScrollingService {
  constructor(
    private promptService: PromptService,
    private translationPipe: TranslationPipe,
  ) {}

  private data: TInfiniteScrollData = {};

  clear(): void {
    this.data = {};
  }

  fetchData<T>(
    apiKey: string,
    request: TInfiniteScrollRequest,
    query: TInfiniteScrollQuery,
    reset?: boolean,
  ): Observable<T> {
    if (reset) {
      this.resetRequestData(apiKey);
    }

    return this.loadMore<T>(apiKey, request, query);
  }

  getRequestData(apiKey: string): TInfiniteScrollingRequestData {
    if (!this.data[apiKey]) {
      this.data[apiKey] = {
        loading: false,
        data: [],
        nextCursor: null,
        hasNext: false,
        totalCount: 0,
        pageSize: 0,
      };
    }

    return this.data[apiKey];
  }

  private resetRequestData(apiKey: string): void {
    const requestData = this.getRequestData(apiKey);
    requestData.nextCursor = null;
    requestData.data = [];
    requestData.totalCount = 0;
    requestData.loading = false;
    requestData.hasNext = true;
  }

  private loadMore<T>(
    apiKey: string,
    request: TInfiniteScrollRequest,
    query: TInfiniteScrollQuery,
  ): Observable<T> {
    const requestData = this.getRequestData(apiKey);

    if (requestData.loading || !requestData.hasNext) {
      return of(requestData) as Observable<T>;
    }

    requestData.loading = true;

    const body: TInfiniteScrollQuery = {
      ...query,
      pagination: {
        ...query.pagination,
        cursor: requestData.nextCursor,
      },
    };

    return request(body).pipe(
      tap((response) => {
        this.handleResponse(apiKey, response);
      }),
      catchError((error) => {
        this.handleError(error);
        requestData.loading = false;
        requestData.hasNext = false;

        return of(requestData);
      }),
      map(() => this.data[apiKey] as T),
    );
  }

  private handleError(error: HttpErrorResponse): void {
    logErrorInSentry({ error, message: 'Failed to load infinite scrolling data' });

    this.promptService.showError(this.translationPipe.transform('prompt_data_loading_fail'));
  }

  private handleResponse(apiKey: string, response: TPointSearchResponse): void {
    this.data[apiKey] = {
      ...this.data[apiKey],
      loading: false,
      data: this.mergeUnique(this.data[apiKey].data, response.data),
      nextCursor: response.nextCursor,
      hasNext: response.hasNext,
      totalCount: response.totalCount,
    };
  }

  private mergeUnique<T extends { _id?: string }>(existing: T[], incoming: T[]): T[] {
    const byId = new Map<string, T>();
    const withoutId: T[] = [];

    const accumulateUniqueItems = (item: T): void => {
      if (item?._id) {
        byId.set(item._id, item);
      } else {
        withoutId.push(item);
      }
    };

    existing.forEach(accumulateUniqueItems);
    incoming.forEach(accumulateUniqueItems);

    return [...byId.values(), ...withoutId];
  }
}
